"use client"

import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { handleProductFormSubmit } from '../model/useProductFormSubmit';
import { normalizeAccessoryGroupId } from '../model/productFormHelpers';
import toast from '../../../shared/lib/toast';

// Таблиця характеристик на сторінці товару — довільні рядки «назва / значення».
// Ключ із префіксом spec: відрізняє їх від старих характеристик (kind, gender,
// collection, composition), які тепер виражені категоріями та блоками опису
// і в таблиці більше не показуються.
export const SPEC_ROW_KEY_PREFIX = 'spec:';

export const createSpecRow = () => ({
    title: { ua: '', en: '' },
    value: { ua: '', en: '' },
});

const buildInitialSpecRows = (initialData) => {
    const list = Array.isArray(initialData?.characteristics)
        ? initialData.characteristics
        : [];

    return list
        .filter((item) => String(item?.key || '').startsWith(SPEC_ROW_KEY_PREFIX))
        .map((item) => ({
            title: { ua: item?.title?.ua || '', en: item?.title?.en || '' },
            value: {
                ua: item?.value?.label?.ua || '',
                en: item?.value?.label?.en || '',
            },
        }));
};

// Зберігаємо як type: 'select' із локалізованим label, а не як 'string':
// нормалізатор на сервері для рядкового типу схлопує локалізований об'єкт
// в один рядок, і англійська версія значення губиться.
const buildSpecRowsPayload = (rows) => {
    const list = Array.isArray(rows) ? rows : [];

    return list
        .map((row, index) => {
            const titleUa = String(row?.title?.ua || '').trim();
            const titleEn = String(row?.title?.en || '').trim();
            const valueUa = String(row?.value?.ua || '').trim();
            const valueEn = String(row?.value?.en || '').trim();

            if (!titleUa && !titleEn) return null;
            if (!valueUa && !valueEn) return null;

            return {
                key: `${SPEC_ROW_KEY_PREFIX}${index + 1}`,
                title: { ua: titleUa, en: titleEn || titleUa },
                type: 'select',
                unit: null,
                value: {
                    value: valueUa || valueEn,
                    label: { ua: valueUa, en: valueEn || valueUa },
                },
                values: [],
            };
        })
        .filter(Boolean);
};

// Блок «Опис» на сторінці товару формується з поля description і тут не
// редагується — інакше знову з'явилося б два місця для одного тексту, через
// що правка опису в адмінці не доїжджала до сайту.
export const DESCRIPTION_SECTION_KEY = 'description';

// Пресети під блоки, які реально використовує сторінка товару. Ключ має
// збігатися з тим, що вже лежить у базі, інакше замість оновлення блоку
// з'явиться другий такий самий.
export const CONTENT_SECTION_PRESETS = [
    { key: 'composition', title: { ua: 'Склад', en: 'Composition' } },
    { key: 'care', title: { ua: 'Догляд', en: 'Care' } },
];

export const createContentSection = (preset = {}) => ({
    key: preset.key || '',
    title: { ua: preset.title?.ua || '', en: preset.title?.en || '' },
    content: { ua: '', en: '' },
});

const buildInitialContentSections = (initialData) => {
    const list = Array.isArray(initialData?.contentSections)
        ? initialData.contentSections
        : [];

    return list
        .filter((section) => String(section?.key || '').trim() !== DESCRIPTION_SECTION_KEY)
        .sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0))
        .map((section) => ({
            key: String(section?.key || '').trim(),
            title: { ua: section?.title?.ua || '', en: section?.title?.en || '' },
            content: { ua: section?.content?.ua || '', en: section?.content?.en || '' },
        }));
};

const normalizeContentSectionsForPayload = (sections) => {
    const list = Array.isArray(sections) ? sections : [];

    return list
        .map((section, index) => ({
            key: String(section?.key || '').trim(),
            title: { ua: section?.title?.ua || '', en: section?.title?.en || '' },
            content: { ua: section?.content?.ua || '', en: section?.content?.en || '' },
            sort: index,
        }))
        .filter(
            (section) =>
                section.key &&
                section.key !== DESCRIPTION_SECTION_KEY &&
                (section.content.ua.trim() || section.content.en.trim()),
        );
};

// Збирає ВСІ оффери з незаповненими осями варіацій разом із переліком осей,
// щоб показати клієнту в модалці підтвердження (де саме і що не заповнено).
const collectUnfilledOffers = (values) => {
    const effectiveAxes = mergeVariationAxes(
        values.variationAxes || [],
        buildAxesFromOffers(values.offers || [])
    );
    const requiredAxes = effectiveAxes.filter(
        (axis) => Array.isArray(axis?.valuesPreset) && axis.valuesPreset.length > 0
    );
    if (!requiredAxes.length) return [];

    const result = [];
    (values.offers || []).forEach((offer, index) => {
        const options = offer?.options || offer?.optionMap || {};
        const missing = requiredAxes.filter((axis) => {
            const value = options?.[axis.axisId];
            return value === undefined || value === null || String(value).trim() === '';
        });
        if (missing.length) {
            result.push({
                variantNo: index + 1,
                sku: offer?.sku || '',
                axes: missing.map((axis) => axis.title?.ua || axis.axisId),
            });
        }
    });
    return result;
};

const AXIS_TITLE_FALLBACKS = {
    variant: { ua: 'Варіант', en: 'Variant' },
    A1: { ua: 'Колір', en: 'Color' },
    A2: { ua: 'Розмір устілки', en: 'Insole size' },
    A3: { ua: 'Форма підборів', en: 'Heel shape' },
    A4: { ua: 'Висота підборів', en: 'Heel height' },
    A5: { ua: 'Тип підошви', en: 'Sole type' },
    A6: { ua: 'Розмір', en: 'Size' },
    A7: { ua: 'Матеріал', en: 'Material' },
};

const AXIS_META_FALLBACKS = {
    A2: { type: 'number', unit: 'cm' },
    A4: { type: 'number', unit: 'cm' },
};

// Розмір устілки (A2): значення понад 28 см не пропонуємо для вибору ніде в адмінці.
const INSOLE_AXIS_ID = 'A2';
const INSOLE_MAX_CM = 28;

const isInsoleAxis = (axisId, title) => {
    if (String(axisId || '').trim() === INSOLE_AXIS_ID) return true;
    const text = `${title?.ua || ''} ${title?.en || ''}`.toLowerCase();
    return text.includes('устіл') || text.includes('стельк') || text.includes('insole');
};

const exceedsInsoleLimit = (value) => {
    const num = Number(String(value ?? '').replace(',', '.'));
    return Number.isFinite(num) && num > INSOLE_MAX_CM;
};

const normalizePreset = (preset) => {
    if (preset && typeof preset === 'object') {
        const value = String(preset.value ?? '').trim();
        if (!value) return null;
        return {
            value,
            label: {
                ua: String(preset.label?.ua || preset.label || value).trim(),
                en: String(preset.label?.en || preset.label?.ua || preset.label || value).trim(),
            },
        };
    }

    const value = String(preset ?? '').trim();
    if (!value) return null;
    return {
        value,
        label: {
            ua: value,
            en: value,
        },
    };
};

const normalizeAxis = (axis = {}) => {
    const axisId = String(axis.axisId || '').trim();
    if (!axisId) return null;

    const titleFallback = AXIS_TITLE_FALLBACKS[axisId] || { ua: axisId, en: axisId };
    const metaFallback = AXIS_META_FALLBACKS[axisId] || {};
    const presetMap = new Map();

    const dropInsoleOverLimit = isInsoleAxis(axisId, axis.title);

    (Array.isArray(axis.valuesPreset) ? axis.valuesPreset : []).forEach((preset) => {
        const normalized = normalizePreset(preset);
        if (!normalized) return;
        if (dropInsoleOverLimit && exceedsInsoleLimit(normalized.value)) return;
        if (!presetMap.has(normalized.value)) {
            presetMap.set(normalized.value, normalized);
        }
    });

    return {
        axisId,
        title: {
            ua: String(axis.title?.ua || titleFallback.ua).trim(),
            en: String(axis.title?.en || axis.title?.ua || titleFallback.en).trim(),
        },
        type: axis.type || metaFallback.type || 'select',
        unit: axis.unit ?? metaFallback.unit ?? null,
        valuesPreset: [...presetMap.values()],
    };
};

const mergeVariationAxes = (...sources) => {
    const axisMap = new Map();

    sources.flat().forEach((rawAxis) => {
        const axis = normalizeAxis(rawAxis);
        if (!axis) return;

        const current = axisMap.get(axis.axisId);
        if (!current) {
            axisMap.set(axis.axisId, axis);
            return;
        }

        // При злитті пресетів з різних джерел (група — з лейблами, оффери —
        // лише value) НЕ можна, щоб value-only перекривав осмислений лейбл.
        // Тому для одного value лишаємо варіант, у якого label не дорівнює value.
        const presetMap = new Map();
        for (const preset of [...current.valuesPreset, ...axis.valuesPreset]) {
            const existing = presetMap.get(preset.value);
            if (!existing) {
                presetMap.set(preset.value, preset);
                continue;
            }
            const existingHasLabel = existing.label?.ua && existing.label.ua !== existing.value;
            const incomingHasLabel = preset.label?.ua && preset.label.ua !== preset.value;
            if (!existingHasLabel && incomingHasLabel) {
                presetMap.set(preset.value, preset);
            }
        }

        axisMap.set(axis.axisId, {
            axisId: axis.axisId,
            title: {
                ua: current.title?.ua || axis.title?.ua || axis.axisId,
                en: current.title?.en || axis.title?.en || current.title?.ua || axis.title?.ua || axis.axisId,
            },
            type: current.type || axis.type || 'select',
            unit: current.unit ?? axis.unit ?? null,
            valuesPreset: [...presetMap.values()],
        });
    });

    return [...axisMap.values()];
};

const buildAxesFromOffers = (offers = []) => {
    const axisMap = new Map();

    offers.forEach((offer) => {
        Object.entries(offer?.optionMap || offer?.options || {}).forEach(([axisId, rawValue]) => {
            const normalizedAxis = normalizeAxis({
                axisId,
                valuesPreset: rawValue === undefined || rawValue === null || rawValue === '' ? [] : [rawValue],
            });
            if (!normalizedAxis) return;

            const current = axisMap.get(axisId);
            axisMap.set(axisId, mergeVariationAxes(current ? [current] : [], [normalizedAxis])[0]);
        });
    });

    return [...axisMap.values()];
};

const pruneUnusedOptionalAxes = (axes = [], offers = []) => {
    const usedAxisIds = new Set();

    offers.forEach((offer) => {
        Object.entries(offer?.optionMap || offer?.options || {}).forEach(([axisId, rawValue]) => {
            if (rawValue === undefined || rawValue === null || rawValue === '') return;
            usedAxisIds.add(String(axisId));
        });
    });

    return axes.filter((axis) => {
        const isOptionalEmptySelectAxis =
            axis?.type === 'select' &&
            (!Array.isArray(axis?.valuesPreset) || axis.valuesPreset.length === 0);

        if (!isOptionalEmptySelectAxis) return true;
        return usedAxisIds.has(String(axis.axisId));
    });
};

export const buildEffectiveVariationAxes = ({ initialData, variationsData, categoryAxes = [] }) => {
    return pruneUnusedOptionalAxes(mergeVariationAxes(
        initialData?.rootVariationTemplate || [],
        initialData?.variationTemplate || [],
        variationsData?.variationAxes || [],
        buildAxesFromOffers(variationsData?.items || []),
        categoryAxes || []
    ), variationsData?.items || []);
};

const DEFAULT_OFFER_STOCKS = [
    {
        warehouseId: '69e788239448d4a5e734305a', // KYIV_MAIN (реальний склад, isDefault)
        onHand: 10,
        reserved: 0,
    },
];

const normalizeOfferImage = (image) => (typeof image === 'string' ? image.trim() : '');
const normalizeOfferImages = (images) => {
    if (!Array.isArray(images)) return [];
    return images
        .map((image) => (typeof image === 'string' ? image.trim() : ''))
        .filter(Boolean)
        .slice(0, 5);
};
const normalizeGroupVideoUrl = (values = {}) => {
    const source = values || {};
    return normalizeOfferImage(source.videoURL ?? source.videoUrl ?? source.video);
};

const DEFAULT_GROUP_IMAGE_URL = "https://i.postimg.cc/8k2LRmzP/fallback.webp";

// Заглавне фото картки = перше фото першого оффера (як показує форма й каталог).
// Каталог рендерить саме group.imageURL, тому тримаємо його в синхроні з фото картки.
const resolveCardImageURL = (values = {}) => {
    const offers = Array.isArray(values?.offers) ? values.offers : [];
    for (const offer of offers) {
        const images = normalizeOfferImages(offer?.images);
        const first = images[0] || normalizeOfferImage(offer?.image);
        if (first) return first;
    }
    return String(values?.imageURL || '').trim();
};

const DISCOUNT_TYPES = ['none', 'percent', 'amount'];

const normalizeOfferDiscountState = (offer = {}) => {
    const discountType = DISCOUNT_TYPES.includes(offer?.discountType) ? offer.discountType : 'none';
    const discount = discountType === 'percent' ? Math.max(0, Number(offer?.discount) || 0) : 0;
    const discountUAH = discountType === 'amount' ? Math.max(0, Number(offer?.discountUAH) || 0) : 0;
    return { discountType, discount, discountUAH };
};

// Comparable state decides whether an offer needs a FULL `patchAdminCatalogOffer`
// (which re-runs the server's optionKey/SKU uniqueness guards). Accessories are
// intentionally excluded: they have a dedicated patch path
// (`patchAdminCatalogOfferAccessories`), so an accessory-only change must NOT
// trigger a full offer re-save — otherwise picking "Аксесуар для всіх офферів"
// would spuriously trip the duplicate-variant guard on groups whose offers share
// an optionKey.
const buildOfferComparableState = (offer = {}) => {
    const images = normalizeOfferImages(offer?.images);
    const fallbackImage = normalizeOfferImage(offer?.image ?? offer?.img);
    const discountState = normalizeOfferDiscountState(offer);

    return {
        sku: String(offer?.sku || '').trim(),
        price: Number(offer?.price),
        opt_price: offer?.opt_price == null || offer?.opt_price === '' ? null : Number(offer.opt_price),
        ...discountState,
        available: offer?.available !== false,
        img: images[0] || fallbackImage,
        images: images.length ? images : (fallbackImage ? [fallbackImage] : []),
        video: normalizeOfferImage(offer?.video ?? offer?.videoUrl),
        optionMap: offer?.options || offer?.optionMap || {},
    };
};

const buildOfferCreatePayload = (offer = {}) => ({
    ...buildOfferComparableState(offer),
    stocks: Array.isArray(offer?.stocks) && offer.stocks.length ? offer.stocks : DEFAULT_OFFER_STOCKS,
    characteristics: Array.isArray(offer?.characteristics) ? offer.characteristics : [],
});

const buildOfferUpdatePayload = (offer = {}) => buildOfferComparableState(offer);

const areOffersEqual = (leftOffer, rightOffer) => (
    JSON.stringify(buildOfferComparableState(leftOffer)) === JSON.stringify(buildOfferComparableState(rightOffer))
);

const resolveGroupAccessoryGroupId = (offers = []) => {
    const accessoryIds = offers
        .map((offer) => normalizeAccessoryGroupId(offer))
        .filter(Boolean);

    if (!accessoryIds.length) return '';

    const uniqueIds = [...new Set(accessoryIds)];
    return uniqueIds.length === 1 ? uniqueIds[0] : '';
};

const formatPayload = (values) => {
    const effectiveAxes = mergeVariationAxes(
        values.variationAxes || [],
        buildAxesFromOffers(values.offers || [])
    );

    const categoryIds = Array.isArray(values.categoryIds)
        ? values.categoryIds.filter(Boolean)
        : [];

    return ({
    slug: values.slug,
    categoryIds,
    status: values.status || "active",
    // Заглавне фото картки — окреме поле; пріоритет у явного imageURL, перше фото
    // офера лише як дефолт для нових товарів (щоб не перезаписувати картку офером).
    imageURL: values.imageURL || resolveCardImageURL(values) || DEFAULT_GROUP_IMAGE_URL,
    videoURL: normalizeGroupVideoUrl(values),
    colorOrder: Array.isArray(values.colorOrder) ? values.colorOrder : [],
    title: values.title,
    subtitle: values.subtitle,
    description: values.description,
    contentSections: normalizeContentSectionsForPayload(values.contentSections),
    // Надсилаємо лише рядки таблиці. Службові характеристики
    // (kind/gender/collection/composition) сервер домерджує з бази —
    // у формі їх немає, і проста заміна їх би стерла.
    characteristics: buildSpecRowsPayload(values.specRows),
    sizeChart: {
        imageUrl: values.sizeChart?.imageUrl || ""
    },
    variationAxes: effectiveAxes.map(axis => ({
        axisId: axis.axisId,
        title: {
            ua: axis.title?.ua || "",
            en: axis.title?.en || ""
        },
        type: axis.type || "select",
        unit: axis.unit || null,
        valuesPreset: axis.valuesPreset || []
    })),
    offers: values.offers.map((offer) => {
        const normalizedImages = normalizeOfferImages(offer.images);
        const fallbackImage = normalizeOfferImage(offer.image);
        const images = normalizedImages.length
            ? normalizedImages
            : (fallbackImage ? [fallbackImage] : []);

        return {
            ...(offer._id && { _id: offer._id }),
            sku: offer.sku,
            price: Number(offer.price),
            opt_price: null,
            ...normalizeOfferDiscountState(offer),
            available: offer.available !== false,
            img: images[0] || '',
            images,
            video: normalizeOfferImage(offer.video),
            optionMap: offer.options || {},
            stocks: Array.isArray(offer.stocks) && offer.stocks.length ? offer.stocks : DEFAULT_OFFER_STOCKS,
            characteristics: []
        };
    })
    });
};

const buildGroupPatchPayload = (values) => ({
    slug: values.slug,
    categoryIds: values.categoryIds,
    status: values.status || "active",
    imageURL: values.imageURL || resolveCardImageURL(values) || DEFAULT_GROUP_IMAGE_URL,
    videoURL: normalizeGroupVideoUrl(values),
    colorOrder: Array.isArray(values.colorOrder) ? values.colorOrder : [],
    title: values.title,
    subtitle: values.subtitle,
    description: values.description,
    contentSections: normalizeContentSectionsForPayload(values.contentSections),
    // Надсилаємо лише рядки таблиці. Службові характеристики
    // (kind/gender/collection/composition) сервер домерджує з бази —
    // у формі їх немає, і проста заміна їх би стерла.
    characteristics: buildSpecRowsPayload(values.specRows),
    sizeChart: {
        imageUrl: values.sizeChart?.imageUrl || ""
    },
});

export const useProductForm = (type, initialData, variationsData) => {
    const navigate = useNavigate();

    // Модалка підтвердження для офферів з незаповненими осями.
    const [unfilledModal, setUnfilledModal] = useState(null);
    const unfilledResolverRef = useRef(null);

    const resolveUnfilledModal = (confirmed) => {
        const resolve = unfilledResolverRef.current;
        unfilledResolverRef.current = null;
        setUnfilledModal(null);
        if (resolve) resolve(Boolean(confirmed));
    };

    const defaultAxes = buildEffectiveVariationAxes({ initialData, variationsData });

    const defaultOffers = variationsData?.items?.length
        ? variationsData.items.map((offer) => {
            const normalizedImages = normalizeOfferImages(offer.images);
            const fallbackImage = normalizeOfferImage(offer.img);
            const images = normalizedImages.length
                ? normalizedImages
                : (fallbackImage ? [fallbackImage] : []);

            return {
                _id: offer._id,
                title: offer.optionKey || '',
                price: offer.price || '',
                sku: offer.sku || '',
                ...normalizeOfferDiscountState(offer),
                available: offer.available !== false,
                options: offer.optionMap || {},
                images,
                image: images[0] || '',
                video: normalizeOfferImage(offer.video),
                accessoryGroupId: normalizeAccessoryGroupId(offer),
            };
        })
        : [];
    const defaultGroupAccessoryOfferId = resolveGroupAccessoryGroupId(defaultOffers);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            slug: initialData?.slug || '',
            title: {
                ua: initialData?.title?.ua || '',
                en: initialData?.title?.en || ''
            },
            subtitle: {
                ua: initialData?.subtitle?.ua || '',
                en: initialData?.subtitle?.en || ''
            },
            description: {
                ua: initialData?.description?.ua || '',
                en: initialData?.description?.en || ''
            },
            categoryIds: Array.isArray(initialData?.categoryIds)
                ? initialData.categoryIds.map(id => String(id._id || id || '')).filter(Boolean)
                : [],
            status: initialData?.status || 'active',
            imageURL: initialData?.imageURL || '',
            videoURL: normalizeGroupVideoUrl(initialData),
            sizeChart: {
                imageUrl: initialData?.sizeChart?.imageUrl || ''
            },
            contentSections: buildInitialContentSections(initialData),
            specRows: buildInitialSpecRows(initialData),
            variationAxes: defaultAxes,
            offers: defaultOffers,
            colorOrder: Array.isArray(initialData?.colorOrder) ? initialData.colorOrder : [],
            groupAccessoryGroupId: defaultGroupAccessoryOfferId,
        },
        validationSchema: Yup.object({
            slug: Yup.string()
                .matches(/^[a-z0-9-]+$/, 'Слаг може містити лише маленькі латинські літери, цифри та дефіс')
                .required("Обов'язкове поле"),
            title: Yup.object({
                ua: Yup.string().min(3, 'Мінімум 3 символи').required("Обов'язкове поле (UA)"),
                en: Yup.string().min(3, 'Мінімум 3 символи').required("Обов'язкове поле (EN)"),
            }),
            categoryIds: Yup.array()
                .of(Yup.string().required())
                .min(1, 'Виберіть хоча б одну категорію')
                .required('Виберіть хоча б одну категорію'),
            status: Yup.string().oneOf(['active', 'hidden', 'draft']).required("Обов'язкове поле"),
            subtitle: Yup.object({
                ua: Yup.string().max(200, 'Не більше 200 символів').required("Обов'язкове поле (UA)"),
                en: Yup.string().max(200, 'Не більше 200 символів').required("Обов'язкове поле (EN)"),
            }),
            description: Yup.object({
                ua: Yup.string().max(1000, 'Не більше 1000 символів').required("Обов'язкове поле (UA)"),
                en: Yup.string().max(1000, 'Не більше 1000 символів').required("Обов'язкове поле (EN)"),
            }),
            offers: Yup.array().of(
                Yup.object().shape({
                    price: Yup.number()
                        .typeError('Ціна має бути числом')
                        .required("Обов'язкове поле"),
                    sku: Yup.string().required('Введіть артикул'),
                })
            )
        }),
        onSubmit: async (values) => {
            // Незаповнені осі варіацій НЕ блокують збереження. Якщо такі є —
            // показуємо модалку підтвердження з розгортним переліком (де саме
            // і що не заповнено) і чекаємо рішення клієнта.
            const unfilledOffers = collectUnfilledOffers(values);
            if (unfilledOffers.length > 0) {
                const confirmed = await new Promise((resolve) => {
                    unfilledResolverRef.current = resolve;
                    setUnfilledModal({ offers: unfilledOffers });
                });
                if (!confirmed) return; // клієнт обрав «Скасувати» — нічого не зберігаємо
            }

            await handleProductFormSubmit({
                type,
                values,
                initialData,
                variationsData,
                navigate,
                formatPayload,
                buildGroupPatchPayload,
                buildOfferCreatePayload,
                buildOfferUpdatePayload,
                areOffersEqual,
            });
        },
    });

    // Повідомляємо користувача, якщо форму не вдалося відправити через помилки валідації
    useEffect(() => {
        if (formik.submitCount > 0 && !formik.isValid) {
            toast.error('Будь ласка, перевірте правильність заповнення полів товару');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.submitCount]);

    return { formik, unfilledModal, resolveUnfilledModal };
};
